import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

// Create Context for Admin State
const AdminContext = createContext();

// AdminProvider component that will wrap your app and provide global state
export const AdminProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null); // For storing admin data
  const [isAuthenticated, setIsAuthenticated] = useState(false); // Admin authentication status
  const [loading, setLoading] = useState(true); // Loading state for initial data fetching
  const [error, setError] = useState(null); // For handling errors

  console.log("✅ AdminProvider initialized");

  // Fetch admin data and check authentication status
  useEffect(() => {
    console.log("🔍 Checking existing employeeToken in localStorage...");

    const token = localStorage.getItem("employeeToken");
    if (!token) {
      console.log("❌ No token found in localStorage — setting isAuthenticated to false");
      setIsAuthenticated(false);
      setLoading(false);
      return;
    }

    console.log("✅ Token found: ", token);
    console.log("🔍 Attempting to fetch admin data using token...");

    const fetchAdminData = async () => {
      try {
        console.log("📡 Making API request to /api/admin/me with token...");
        const response = await axios.get('/api/admin/me', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        console.log("✅ Admin data fetched successfully:", response.data);

        // Set admin data and authentication status
        setAdmin(response.data);
        setIsAuthenticated(true);
        console.log("✅ Admin data set successfully — isAuthenticated set to true");
      } catch (err) {
        console.error("❌ Failed to fetch admin data:", err);
        setError('Failed to fetch admin data');
        setIsAuthenticated(false);
        console.log("❌ Authentication failed — isAuthenticated set to false");
      } finally {
        setLoading(false);
        console.log("⚡ Loading state set to false");
      }
    };

    fetchAdminData();
  }, []); // Run only once on component mount

  // Login handler
  const login = (adminData, token) => {
    console.log("🔐 Logging in admin with data:", adminData);

    // Store token in localStorage
    console.log("📝 Storing token in localStorage...");
    localStorage.setItem("employeeToken", token);
    console.log("✅ Token stored: employeeToken");

    // Set the admin data and authenticated status
    setAdmin(adminData);
    setIsAuthenticated(true);

    console.log("✅ Login complete — admin data set and isAuthenticated set to true");
  };

  // Logout handler
  const logout = () => {
    console.log("🚪 Logging out admin...");

    // Remove token from localStorage
    console.log("🗑️ Removing employeeToken from localStorage...");
    localStorage.removeItem("employeeToken");
    console.log("✅ Token removed from localStorage");

    // Clear admin data and set isAuthenticated to false
    setAdmin(null);
    setIsAuthenticated(false);
    console.log("✅ Logout complete — admin data cleared and isAuthenticated set to false");
  };

  return (
    <AdminContext.Provider
      value={{
        admin,
        isAuthenticated,
        loading,
        error,
        login,
        logout,
      }}
    >
      {children}
    </AdminContext.Provider>
  );
};

// Custom hook to use AdminContext
export const useAdmin = () => {
  return useContext(AdminContext);
};
